/**
 * "Is there a LinkedIn sending seat left?" — one answer for every caller (PREC-3787).
 *
 * The seat numbers arrive from `GET /unipile/slots-available`, whose `slotsAvailable` field is
 * badly named: it is the **used** count, not the free one (the Users page card reads
 * "{slotsAvailable} / {slotAmount} seats used"). Re-deriving that per caller is how the rule drifts,
 * so both the Users-table dropdown and the invite modal read it from here.
 *
 * A sending seat also carries **one included email address** — `emailBaseAmount` tracks
 * `slotAmount` in the plan tiers — which the invite modal states so an admin can see what the
 * choice grants beyond LinkedIn.
 */

/** The subset of the slots-available payload the seat maths needs. */
export interface SendingSeatSlotData {
	/** Sending users today. Named "available", means "used" — see the module comment. */
	slotsAvailable?: number;
	/** Total LinkedIn sending seats the company has (plan + paid + bonus). */
	slotAmount?: number;
	/** Email addresses connected today. */
	emailsUsed?: number;
	/** Total email addresses the company has. */
	emailsAmount?: number;
}

export interface SendingSeatAvailability {
	used: number;
	capacity: number;
	/** Never negative: a company sitting above capacity has no headroom, not negative headroom. */
	remaining: number;
	hasFreeSeat: boolean;
}

/**
 * `used >= capacity`, not `===`: a company can sit **above** its capacity after a plan downgrade,
 * or from seats granted before the cap was enforced server-side. An exact match would then read as
 * "room left" and let a promotion through to a 409 from `assertSeatAvailable`.
 *
 * Missing numbers (the query has not resolved, or the API's error path returned a partial) collapse
 * to 0/0 — which reports *no* free seat, so an unknown state never invites someone into a seat the
 * company may not have.
 */
export const sendingSeatAvailability = (
	slotData?: SendingSeatSlotData | null
): SendingSeatAvailability => {
	const used = slotData?.slotsAvailable ?? 0;
	const capacity = slotData?.slotAmount ?? 0;

	return {
		used,
		capacity,
		remaining: Math.max(0, capacity - used),
		hasFreeSeat: used < capacity,
	};
};

/** Email addresses a sending seat includes, for the "and one email address" line in the modal. */
export const emailSeatUsage = (
	slotData?: SendingSeatSlotData | null
): { used: number; capacity: number } => ({
	used: slotData?.emailsUsed ?? 0,
	capacity: slotData?.emailsAmount ?? 0,
});
